// src/UserDashboard.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import FullScreenLoader from './components/LibraryPattern.jsx';

// صور "حول المكتبة"
import logoImg from './assets/logo.png';
import footer1Img from './assets/footer1.png';
import footer2Img from './assets/footer2.png';

export default function UserDashboard() {
  const { barcode } = useParams();
  const navigate = useNavigate();

  // 🎯 وضع الضيف: ادخل لهذه الصفحة عبر مسار مثل /user/guest
  const isGuest =
    !barcode ||
    String(barcode).toLowerCase() === 'guest' ||
    String(barcode).toLowerCase() === 'visitor' ||
    String(barcode).toLowerCase() === 'guest-mode';

  const [user, setUser] = useState(null);
  const [availableBooks, setAvailableBooks] = useState([]);
  const [borrowedBooks, setBorrowedBooks] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState(''); // تُعرض فقط تحت زر الحجز داخل النافذة

  // قائمة الموبايل + التبويب النشط
  const [navOpen, setNavOpen] = useState(false);
  const [active, setActive] = useState('profile'); // profile | available | borrowed | reservations | about

  // بحث وفرز
  const [searchTerm, setSearchTerm] = useState('');
  const [genreFilter, setGenreFilter] = useState(''); // فارغ = كل الفئات

  // نافذة تفاصيل الكتاب
  const [selectedBook, setSelectedBook] = useState(null);

  // ألوان وهوية
  const colors = {
    bg: '#ffffff',
    card: '#ffffff',
    white: '#ffffff',
    yellow: '#FFD700',
    orange: '#FF8C00',
    border: '#E5E7EB',
    muted: '#6B7280',
    text: '#111111'
  };

  const placeholderCover =
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="560">
        <rect width="100%" height="100%" fill="#ffffff"/>
        <rect x="20" y="20" width="360" height="520" rx="12" fill="#f8fafc" stroke="#FF8C00" stroke-width="3"/>
        <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#111" font-size="22" font-family="Arial">
          لا توجد صورة غلاف
        </text>
      </svg>`
    );

  // 👤 أفاتار افتراضي للمستخدم/الضيف
  const guestAvatar =
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="160" height="160">
        <defs>
          <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stop-color="#FFEDD5"/>
            <stop offset="100%" stop-color="#FDE68A"/>
          </linearGradient>
        </defs>
        <rect width="100%" height="100%" rx="80" fill="url(#g)"/>
        <g transform="translate(80,84)">
          <circle cx="0" cy="-36" r="28" fill="#111"/>
          <path d="M-52,36 a52,52 0 1,0 104,0 a36,36 0 1,0 -104,0" fill="#111"/>
        </g>
      </svg>`
    );

  // URL نهائي لصورة المستخدم
  const getAvatarSrc = (u, guest = false) => {
    if (guest) return guestAvatar;
    const p = u?.profilePic;
    if (!p) return guestAvatar;
    return `/library-api${p}`;
  };

  // 🔎 لجلب الكتب المتاحة في وضع الضيف
  async function fetchGuestBooks() {
    try {
      const { data } = await axios.get('/library-api/api/books/available');
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.books)) return data.books;
    } catch {}
    try {
      const { data } = await axios.get('/library-api/api/books');
      const arr = Array.isArray(data) ? data : Array.isArray(data?.books) ? data.books : [];
      return arr.filter((b) => !b.isBorrowed);
    } catch {
      return [];
    }
  }

  /* ====== محمّل بحد أدنى 5 ثوانٍ + دعم وضع الضيف ====== */
  useEffect(() => {
    let isMounted = true;
    let timeoutId;
    const MIN_LOADER_MS = 5000;
    const start = Date.now();

    (async () => {
      try {
        if (isGuest) {
          if (!isMounted) return;
          setUser({
            name: 'زائر/ضيف',
            phone: '',
            address: '',
            balance: 0
          });
          const guestBooks = await fetchGuestBooks();
          if (!isMounted) return;
          setAvailableBooks(Array.isArray(guestBooks) ? guestBooks : []);
          setBorrowedBooks([]);
          setReservations([]);
        } else {
          const { data } = await axios.get(`/library-api/api/users/full/${barcode}`);
          if (!isMounted) return;
          setUser(data.user);
          setAvailableBooks(Array.isArray(data.availableBooks) ? data.availableBooks : []);
          setBorrowedBooks(Array.isArray(data.borrowedBooks) ? data.borrowedBooks : []);
          setReservations(Array.isArray(data.reservations) ? data.reservations : []);
        }
      } catch {
        if (!isMounted) return;
        setError(isGuest ? 'تعذر تحميل قائمة الكتب للضيف.' : 'لم يتم العثور على مستخدم بهذا الباركود.');
      } finally {
        const elapsed = Date.now() - start;
        const remain = Math.max(0, MIN_LOADER_MS - elapsed);
        timeoutId = setTimeout(() => {
          if (isMounted) setLoading(false);
        }, remain);
      }
    })();

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [barcode, isGuest]);
  /* ====== نهاية التعديل ====== */

  const handleReserve = async (bookId) => {
    if (isGuest) {
      setMessage('يرجى الحصول على حساب مستخدم من المكتبة لإتمام الحجز.');
      return;
    }
    try {
      const { data } = await axios.post('/library-api/api/reservations', { barcode, bookId });
      setMessage(data.message || 'تم إرسال طلب الحجز بنجاح');
    } catch {
      setMessage('تعذر إرسال طلب الحجز');
    }
  };

  // تنظيف رسالة الأسفل بعد 3 ثواني
  useEffect(() => {
    if (message) {
      const t = setTimeout(() => setMessage(''), 3000);
      return () => clearTimeout(t);
    }
  }, [message]);

  const statusLabel = (s) =>
    s === 'approved' ? '✅ تم القبول'
      : s === 'rejected' ? '❌ تم الرفض'
      : '⏳ بانتظار الموافقة';

  const fmtIQD = (v) => {
    const n = Number(v);
    if (!n) return '—';
    try {
      return new Intl.NumberFormat('ar-IQ', { style: 'currency', currency: 'IQD', maximumFractionDigits: 0 }).format(n);
    } catch {
      return `${n} د.ع`;
    }
  };

  // كتب متاحة فعلًا
  const actuallyAvailable = useMemo(
    () => (availableBooks || []).filter((b) => !b.isBorrowed),
    [availableBooks]
  );

  // كل الفئات
  const allGenres = useMemo(() => {
    const set = new Set();
    for (const b of actuallyAvailable) set.add((b.genre || 'عام').trim());
    return Array.from(set);
  }, [actuallyAvailable]);

  // تجميع حسب النوع (افتراضي)
  const groupedByGenre = useMemo(() => {
    const groups = {};
    for (const b of actuallyAvailable) {
      const g = (b.genre || 'عام').trim();
      if (!groups[g]) groups[g] = [];
      groups[g].push(b);
    }
    return groups;
  }, [actuallyAvailable]);

  // نتائج البحث/الفرز
  const searchResultsByGenre = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const limitToGenre = genreFilter.trim();

    if (!term && !limitToGenre) return null;

    const groups = {};
    for (const b of actuallyAvailable) {
      const g = (b.genre || 'عام').trim();
      if (limitToGenre && g !== limitToGenre) continue;
      const name = (b.name || '').toLowerCase();
      if (term && !name.includes(term)) continue;
      if (!groups[g]) groups[g] = [];
      groups[g].push(b);
    }
    return groups;
  }, [searchTerm, genreFilter, actuallyAvailable]);

  // إغلاق نافذة التفاصيل بـ ESC
  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') setSelectedBook(null); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, []);

  // حالة الحجز لهذا الكتاب
  const selectedReservationStatus = useMemo(() => {
    if (!selectedBook) return null;
    const sid = selectedBook._id || selectedBook.id;
    const r = reservations.find((x) => (x.book?._id || x.bookId) === sid);
    return r?.status || null;
  }, [selectedBook, reservations]);

  if (loading) return <FullScreenLoader userName={user?.name} />;
  if (error) return <p className="text-center mt-10" style={{ color: colors.orange }}>{error}</p>;
  if (!user) return <p className="text-center mt-10" style={{ color: colors.orange }}>❌ لم يتم العثور على مستخدم.</p>;

  // ===== ستايلات =====
  const styles = {
    page: { backgroundColor: colors.bg, minHeight: '100vh', color: colors.text },
    layout: { gridTemplateColumns: '280px 1fr', gap: '1rem' },
    sidebar: {
      backgroundColor: colors.card,
      borderLeft: `2px solid ${colors.orange}`,
      padding: '1rem',
      position: 'sticky',
      top: 0,
      height: '100dvh',
      overflowY: 'auto'
    },
    navItem: (active_, disabled = false) => ({
      display: 'block',
      width: '100%',
      textAlign: 'right',
      padding: '0.75rem 1rem',
      marginBottom: '0.5rem',
      borderRadius: '12px',
      fontWeight: 700,
      background: active_ ? colors.orange : '#fff',
      color: disabled ? '#9CA3AF' : active_ ? '#fff' : colors.text,
      border: `1px solid ${colors.border}`,
      transition: 'transform .15s ease, box-shadow .15s ease',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1
    }),
    content: { padding: '1rem' },
    card: {
      backgroundColor: colors.card,
      border: `1px solid ${colors.border}`,
      borderRadius: '16px',
      padding: '1rem'
    },
    sectionTitle: {
      fontWeight: 800,
      fontSize: '1.1rem',
      borderBottom: `2px solid ${colors.border}`,
      paddingBottom: '.5rem',
      marginBottom: '1rem',
      color: colors.text,
      textAlign: 'right'
    },
    label: { color: colors.muted },
    badge: {
      display: 'inline-block',
      background: colors.yellow,
      color: colors.text,
      padding: '0.15rem 0.5rem',
      borderRadius: '8px',
      fontWeight: 800,
      marginRight: '.4rem'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
      gap: '0.9rem'
    },
    bookCard: {
      background: '#fff',
      border: `1px solid ${colors.border}`,
      borderRadius: '14px',
      padding: '0.75rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '.5rem',
      boxShadow: '0 4px 10px rgba(0,0,0,.04)',
      transition: 'transform .15s ease, box-shadow .15s ease',
      cursor: 'pointer'
    },
    cover: {
      width: '100%',
      aspectRatio: '3/4',
      objectFit: 'cover',
      borderRadius: '10px',
      border: `1px solid ${colors.border}`,
      background: '#f7f7f7',
      transition: 'transform .2s ease'
    },
    bookName: { fontWeight: 800, color: colors.text, fontSize: '0.95rem' },
    price: { color: colors.text, fontWeight: 800 },
    reserveBtn: {
      marginTop: '0.25rem',
      background: colors.orange,
      color: '#fff',
      fontWeight: 800,
      border: 'none',
      borderRadius: '10px',
      padding: '0.6rem .85rem',
      cursor: 'pointer',
      boxShadow: '0 10px 18px rgba(255,140,0,.25)',
      transition: 'transform .2s ease, box-shadow .2s ease'
    },
    reserveBtnDisabled: {
      marginTop: '0.25rem',
      background: '#cbd5e1',
      color: '#555',
      fontWeight: 800,
      border: 'none',
      borderRadius: '10px',
      padding: '0.6rem .85rem',
      cursor: 'not-allowed',
      boxShadow: 'none',
      opacity: 0.9
    },
    topbar: {
      display: 'grid',
      gridTemplateColumns: '100px 1fr 100px',
      alignItems: 'center',
      padding: '1rem',
      borderBottom: `1px solid ${colors.border}`,
      position: 'sticky',
      top: 0,
      zIndex: 10,
      background: colors.bg
    },
    burger: {
      background: 'transparent',
      border: 'none',
      width: 36,
      height: 28,
      position: 'relative',
      cursor: 'pointer'
    },
    burgerBar: {
      position: 'absolute',
      right: 0,
      left: 0,
      height: 3,
      borderRadius: 3,
      background: colors.orange,
      transition: 'transform .25s ease, opacity .25s ease'
    },
    titleShine: {
      fontWeight: 900,
      fontSize: '1.25rem',
      textAlign: 'center',
      fontFamily: `'Cairo','Tajawal','Segoe UI', Arial, sans-serif`,
      backgroundImage: 'linear-gradient(90deg, #111, #FF8C00, #FFD700, #111)',
      backgroundSize: '200% 100%',
      WebkitBackgroundClip: 'text',
      backgroundClip: 'text',
      color: 'transparent',
      animation: 'shine 3s linear infinite',
      textShadow: '0 0 1px rgba(0,0,0,0.15)'
    },
    inlineMsgOk: {
      marginTop: '.5rem',
      padding: '.5rem .6rem',
      background: '#f0fff4',
      color: '#0a7a0a',
      border: '1px solid #b7f7c1',
      borderRadius: 10,
      fontWeight: 700,
      textAlign: 'center'
    },
    inlineMsgErr: {
      marginTop: '.5rem',
      padding: '.5rem .6rem',
      background: '#fff5f5',
      color: '#9b1111',
      border: '1px solid #ffd3d3',
      borderRadius: 10,
      fontWeight: 700,
      textAlign: 'center'
    },
    aboutText: { lineHeight: 1.9, color: colors.text, fontWeight: 500 },
    aboutGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: '1rem',
      marginTop: '0.75rem'
    },
    aboutItem: {
      background: '#fff',
      border: `1px solid ${colors.border}`,
      borderRadius: 12,
      padding: 8,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 6px 14px rgba(0,0,0,.05)'
    },
    aboutImg: { width: '100%', height: 160, objectFit: 'contain' },
    overlay: (show) => ({
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,.25)',
      opacity: show ? 1 : 0,
      pointerEvents: show ? 'auto' : 'none',
      transition: 'opacity .28s ease',
      zIndex: 25
    }),
    drawer: (show) => ({
      position: 'fixed',
      right: 0,
      top: 64,
      bottom: 0,
      width: 280,
      background: colors.card,
      borderLeft: `2px solid ${colors.orange}`,
      transform: show ? 'translateX(0)' : 'translateX(100%)',
      transition: 'transform .28s ease',
      zIndex: 30,
      padding: '1rem',
      boxShadow: '-10px 0 24px rgba(0,0,0,.08)'
    }),
    moreBtn: {
      marginTop: '.25rem',
      background: colors.yellow,
      color: colors.text,
      fontWeight: 800,
      border: 'none',
      borderRadius: '10px',
      padding: '.5rem 1rem',
      cursor: 'pointer',
      boxShadow: '0 8px 16px rgba(0,0,0,.08)',
      transition: 'transform .2s ease, box-shadow .2s ease'
    },
    modalOverlay: {
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,.45)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 50,
      padding: '1rem'
    },
    modalCard: {
      width: 'min(980px, 100%)',
      background: '#fff',
      borderRadius: 16,
      border: `1px solid ${colors.border}`,
      overflow: 'hidden',
      boxShadow: '0 30px 60px rgba(0,0,0,.2)'
    },
    modalHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0.75rem 1rem',
      borderBottom: `1px solid ${colors.border}`,
      background: '#fff'
    },
    backBtn: {
      background: '#fff',
      border: `1px solid ${colors.border}`,
      borderRadius: 10,
      padding: '.35rem .6rem',
      cursor: 'pointer',
      fontWeight: 800,
      transition: 'transform .2s ease'
    },
    modalBody: {
      display: 'grid',
      gridTemplateColumns: '1.3fr 1fr',
      gap: '1rem',
      padding: '1rem'
    },
    modalImg: {
      width: '100%',
      height: 'auto',
      maxHeight: '75vh',
      objectFit: 'contain',
      borderRadius: 12,
      border: `1px solid ${colors.border}`,
      background: '#fff'
    },
    avatarWrap: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      marginBottom: '1rem'
    },
    avatarImg: {
      width: 96,
      height: 96,
      borderRadius: '9999px',
      border: `2px solid ${colors.border}`,
      objectFit: 'cover',
      background: '#fff'
    }
  };

  // شبكة فئة: صف واحد ثم "انقر للمزيد"
  const GenreGrid = ({ list }) => {
    const gridRef = useRef(null);
    const [cols, setCols] = useState(4);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
      const calcCols = () => {
        if (!gridRef.current) return;
        const w = gridRef.current.clientWidth;
        const isMobile = window.innerWidth < 640;
        const min = isMobile ? 150 : 180;
        const gap = 14;
        const c = Math.max(1, Math.floor((w + gap) / (min + gap)));
        setCols(c);
      };
      calcCols();
      window.addEventListener('resize', calcCols);
      return () => window.removeEventListener('resize', calcCols);
    }, []);

    const visible = expanded ? list : list.slice(0, cols);

    return (
      <>
        <div ref={gridRef} style={styles.grid} className="grid-auto">
          {visible.map((book) => {
            const cover = book.coverUrl || placeholderCover;
            return (
              <div
                key={book._id}
                style={styles.bookCard}
                onMouseEnter={(e)=>{ e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 14px 28px rgba(0,0,0,.08)'; }}
                onMouseLeave={(e)=>{ e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow='0 4px 10px rgba(0,0,0,.04)'; }}
                onClick={() => setSelectedBook(book)}
              >
                <img
                  src={cover}
                  alt={`غلاف ${book.name || ''}`}
                  style={styles.cover}
                  onError={(e) => { e.currentTarget.src = placeholderCover; }}
                  onMouseEnter={(e)=>{ e.currentTarget.style.transform='scale(1.01)'; }}
                  onMouseLeave={(e)=>{ e.currentTarget.style.transform=''; }}
                />
                <div className="book-name" style={{ ...styles.bookName }}>
                  {book.name || '—'}
                </div>
              </div>
            );
          })}
        </div>

        {list.length > cols && !expanded && (
          <div style={{ textAlign: 'center' }}>
            <button className="btn-anim" style={styles.moreBtn} onClick={() => setExpanded(true)}>انقر للمزيد</button>
          </div>
        )}
        {list.length > cols && expanded && (
          <div style={{ textAlign: 'center' }}>
            <button className="btn-anim" style={styles.moreBtn} onClick={() => setExpanded(false)}>إخفاء</button>
          </div>
        )}
      </>
    );
  };

  // شريط العنوان + زر الهامبرغر (موبايل فقط)
  const TitleBar = () => (
    <>
      <style>{`
        .only-desktop { display: none; }
        .only-mobile { display: block; }
        @media (min-width: 1024px) {
          .only-desktop { display: grid; }
          .only-mobile { display: none; }
        }
        @media (max-width: 640px) {
          .mobile-content .card { padding: .75rem !important; }
          .mobile-content .section-title { font-size: 1rem !important; margin-bottom: .75rem !important; }
          .mobile-content .grid-auto { gap: .6rem !important; }
          .mobile-content .book-name { font-size: .9rem !important; }
          .mobile-content .about-img { height: 120px !important; }
          .mobile-drawer button { padding: .6rem .8rem !important; font-size: .95rem !important; }
          .title-shine { font-size: 1.05rem !important; }
        }
        @media (max-width: 640px) {
          .modal-card {
            width: 94vw !important;
            max-height: 88vh !important;
            border-radius: 14px !important;
            display: flex !important;
            flex-direction: column !important;
            padding-bottom: env(safe-area-inset-bottom);
          }
          .modal-body {
            grid-template-columns: 1fr !important;
            gap: .75rem !important;
            padding: .75rem !important;
            overflow-y: auto !important;
            -webkit-overflow-scrolling: touch;
          }
          .modal-actions {
            position: sticky;
            bottom: 0;
            background: #fff;
            padding-top: .5rem;
            padding-bottom: calc(.25rem + env(safe-area-inset-bottom));
            margin-top: .75rem;
            border-top: 1px solid #E5E7EB;
          }
          .modal-img {
            max-height: 38vh !important;
            object-fit: contain !important;
            border-radius: 10px !important;
          }
          .modal-header {
            padding: .6rem .8rem !important;
            border-bottom: 1px solid #E5E7EB;
            background: #fff;
          }
        }
        .btn-anim { will-change: transform, box-shadow; }
        .btn-anim:hover { transform: translateY(-2px) scale(1.02); box-shadow: 0 12px 22px rgba(0,0,0,.12) !important; }
        .btn-anim:active { transform: translateY(0) scale(0.98); }
        @keyframes modalIn { from { transform: translateY(8px) scale(.98); opacity: 0 } to { transform: translateY(0) scale(1); opacity: 1 } }
        .modal-card { animation: modalIn .22s ease both; }
        @keyframes shine { 0%{background-position:0% 50%}100%{background-position:200% 50%} }
      `}</style>

      <div style={styles.topbar}>
        <div style={{ width: 100, display: 'flex', justifyContent: 'flex-start' }}>
          <button
            className="only-mobile"
            aria-label="فتح القائمة"
            style={styles.burger}
            onClick={() => setNavOpen(true)}
          >
            <span style={{ ...styles.burgerBar, top: 4 }} />
            <span style={{ ...styles.burgerBar, top: 12 }} />
            <span style={{ ...styles.burgerBar, top: 20 }} />
          </button>
        </div>
        <div className="title-shine" style={styles.titleShine}>
          {isGuest ? 'التصفح كضيف — المكتبة الشبابية المجانية للشباب' : 'المكتبة الشبابية المجانية'}
        </div>
        <div style={{ width: 100 }} />
      </div>
    </>
  );

  // ملاحظة: أزلنا كل استدعاءات زر "سجّل الدخول / امسح الباركود" في وضع الضيف

  return (
    <div className="p-0" dir="rtl" style={styles.page}>
      <TitleBar />

      <div className="container mx-auto px-4 py-6">
        {/* ===== ديسكتوب: سايدبار ثابتة (فقط كبيرة) ===== */}
        <div className="only-desktop" style={styles.layout}>
          {/* Sidebar */}
          <aside style={styles.sidebar}>
            <nav className="space-y-2">
              <button
                className="btn-anim"
                style={styles.navItem(active === 'profile')}
                onClick={() => setActive('profile')}
              >
                {isGuest ? 'معلومات الزائر' : 'بيانات المستخدم'}
              </button>

              <button
                className="btn-anim"
                style={styles.navItem(active === 'available')}
                onClick={() => setActive('available')}
              >
                الكتب المتاحة
              </button>

              <button
                className="btn-anim"
                style={styles.navItem(active === 'borrowed', isGuest)}
                onClick={() => !isGuest && setActive('borrowed')}
                title={isGuest ? 'متاح للمستخدمين المسجلين فقط' : ''}
                disabled={isGuest}
              >
                الكتب المستعارة
              </button>

              <button
                className="btn-anim"
                style={styles.navItem(active === 'reservations', isGuest)}
                onClick={() => !isGuest && setActive('reservations')}
                title={isGuest ? 'متاح للمستخدمين المسجلين فقط' : ''}
                disabled={isGuest}
              >
                حجوزاتك
              </button>

              <button
                className="btn-anim"
                style={styles.navItem(active === 'about')}
                onClick={() => setActive('about')}
              >
                حول المكتبة
              </button>
            </nav>

            {isGuest && (
              <div style={{ marginTop: '1rem', color: colors.muted, fontWeight: 700 }}>
                أنت تتصفح كضيف — لا يمكنك الحجز أو رؤية السجل.
              </div>
            )}
          </aside>

          {/* محتوى الديسكتوب */}
          <main style={styles.content}>
            {active === 'profile' && (
              <section style={styles.card} className="card">
                <h3 style={styles.sectionTitle} className="section-title">
                  {isGuest ? '👋 مرحباً بك، زائر' : '📋 بياناتك'}
                </h3>

                {/* 👤 صورة المستخدم / الضيف */}
                <div style={styles.avatarWrap}>
                  <img
                    src={getAvatarSrc(user, isGuest)}
                    alt={isGuest ? 'صورة ضيف' : `صورة ${user?.name || ''}`}
                    style={styles.avatarImg}
                    onError={(e) => { e.currentTarget.src = guestAvatar; }}
                  />
                  <div>
                    <div style={{ fontWeight: 900, fontSize: '1.05rem' }}>
                      {isGuest ? 'زائر/ضيف' : (user?.name || '—')}
                    </div>
                    <div style={{ color: colors.muted, fontSize: '.9rem' }}>
                      {isGuest ? 'تصفح للاطلاع — لا يمكن الحجز' : 'عضو مسجل في المكتبة'}
                    </div>
                  </div>
                </div>

                {isGuest ? (
                  <div style={{ color: colors.muted, fontWeight: 700, lineHeight: 1.9 }}>
                    يمكنك تصفح جميع الكتب المتاحة، ولكن للحجز والاستعارة تحتاج إلى حساب مستخدم.
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(220px,1fr))',
                      gap: '1rem'
                    }}
                    className="grid-auto"
                  >
                    <div className="card" style={{ ...styles.card, padding: '.9rem' }}>
                      <div style={{ color: colors.muted }}>👤 الاسم</div>
                      <div style={{ fontWeight: 900 }}>{user.name}</div>
                    </div>
                    <div className="card" style={{ ...styles.card, padding: '.9rem' }}>
                      <div style={{ color: colors.muted }}>📱 الهاتف</div>
                      <div style={{ fontWeight: 900 }}>{user.phone || '—'}</div>
                    </div>
                    <div className="card" style={{ ...styles.card, padding: '.9rem' }}>
                      <div style={{ color: colors.muted }}>🏠 العنوان</div>
                      <div style={{ fontWeight: 900 }}>{user.address || '—'}</div>
                    </div>
                    <div className="card" style={{ ...styles.card, padding: '.9rem' }}>
                      <div style={{ color: colors.muted }}>💰 الرصيد</div>
                      <div style={{ fontWeight: 900, color: colors.yellow }}>{fmtIQD(user.balance)}</div>
                    </div>
                  </div>
                )}
              </section>
            )}

            {active === 'available' && (
              <section style={{ ...styles.card, display: 'grid', gap: '1.25rem' }} className="card">
                <h3 style={styles.sectionTitle} className="section-title">📚 الكتب المتاحة</h3>
                <div style={{ display: 'flex', gap: '.6rem', flexWrap: 'wrap', marginBottom: '.6rem' }}>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e)=>setSearchTerm(e.target.value)}
                    placeholder="🔎 ابحث عن اسم كتاب..."
                    style={{
                      flex: '1 1 220px',
                      border: `1px solid ${colors.border}`,
                      borderRadius: 12,
                      padding: '.55rem .75rem',
                      outline: 'none'
                    }}
                  />
                  <select
                    value={genreFilter}
                    onChange={(e)=>setGenreFilter(e.target.value)}
                    style={{
                      minWidth: 160,
                      border: `1px solid ${colors.border}`,
                      borderRadius: 12,
                      padding: '.55rem .75rem',
                      background: '#fff'
                    }}
                  >
                    <option value="">كل الفئات</option>
                    {allGenres.map((g)=>(<option key={g} value={g}>{g}</option>))}
                  </select>
                  {(searchTerm || genreFilter) && (
                    <button
                      className="btn-anim"
                      onClick={()=>{ setSearchTerm(''); setGenreFilter(''); }}
                      style={styles.moreBtn}
                    >
                      مسح التصفية
                    </button>
                  )}
                </div>

                {!searchResultsByGenre ? (
                  Object.entries(groupedByGenre).map(([genre, list]) => (
                    <div key={genre}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.6rem' }}>
                        <h4 style={{ ...styles.sectionTitle, borderBottom: 'none', margin: 0, color: colors.orange }} className="section-title">{genre}</h4>
                        <span style={styles.badge}>{list.length} كتاب</span>
                      </div>
                      <GenreGrid list={list} />
                    </div>
                  ))
                ) : (
                  Object.keys(searchResultsByGenre).length ? (
                    Object.entries(searchResultsByGenre).map(([genre, list]) => (
                      <div key={genre}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.6rem' }}>
                          <h4 style={{ ...styles.sectionTitle, borderBottom: 'none', margin: 0, color: colors.orange }} className="section-title">{genre}</h4>
                          <span style={styles.badge}>{list.length} كتاب</span>
                        </div>
                        <GenreGrid list={list} />
                      </div>
                    ))
                  ) : (
                    <p style={{ color: colors.muted }}>لا يوجد نتائج مطابقة لبحثك.</p>
                  )
                )}
              </section>
            )}

            {active === 'borrowed' && (
              <section style={styles.card} className="card">
                <h3 style={styles.sectionTitle} className="section-title">📕 الكتب المستعارة</h3>
                {isGuest ? (
                  <div style={{ color: colors.muted, fontWeight: 700 }}>
                    هذه الصفحة متاحة فقط للمستخدمين المسجلين.
                  </div>
                ) : borrowedBooks.length ? (
                  <ul className="space-y-2" style={{ listStyle: 'none', padding: 0 }}>
                    {borrowedBooks.map((b, i) => (
                      <li key={i} style={{ color: colors.muted }}>
                        📖 {b.book?.name || '—'} — 🗓️ يرجع:
                        <span style={{ color: colors.yellow, fontWeight: 800, marginRight: 6 }}>
                          {new Date(b.dueDate).toLocaleDateString()}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p style={{ color: colors.muted }}>لا توجد كتب مستعارة.</p>
                )}
              </section>
            )}

            {active === 'reservations' && (
              <section style={styles.card} className="card">
                <h3 style={styles.sectionTitle} className="section-title">📌 حجوزاتك</h3>
                {isGuest ? (
                  <div style={{ color: colors.muted, fontWeight: 700 }}>
                    هذه الصفحة متاحة فقط للمستخدمين المسجلين.
                  </div>
                ) : reservations.length ? (
                  <ul className="space-y-2" style={{ listStyle: 'none', padding: 0 }}>
                    {reservations.map((r, i) => (
                      <li key={i} style={{ color: colors.muted }}>
                        📚 {r.book?.name || '—'} —{' '}
                        <span style={{ color: colors.text, fontWeight: 800 }}>
                          {statusLabel(r.status)}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p style={{ color: colors.muted }}>لا توجد حجوزات بعد.</p>
                )}
              </section>
            )}

            {active === 'about' && (
              <section style={{ ...styles.card }} className="card">
                <h3 style={styles.sectionTitle} className="section-title">ℹ️ حول المكتبة</h3>
                <p style={styles.aboutText}>
                  هذه المنصّة تابعة لـ<strong> منظمة ملهم</strong>، وتهدف إلى إتاحة الوصول
                  إلى المعرفة لكل الفئات مجانًا، عبر أرشفة الكتب، تسهيل الاستعارة والحجز،
                  وتقديم تجربة استخدام بسيطة وسريعة على مختلف الأجهزة. شكراً لدعمكم 🙏
                </p>
                <div style={styles.aboutGrid} className="grid-auto">
                  <div style={styles.aboutItem}><img src={logoImg} alt="شعار ملهم" style={styles.aboutImg} className="about-img" /></div>
                  <div style={styles.aboutItem}><img src={footer1Img} alt="صورة توضيحية 1" style={styles.aboutImg} className="about-img" /></div>
                  <div style={styles.aboutItem}><img src={footer2Img} alt="صورة توضيحية 2" style={styles.aboutImg} className="about-img" /></div>
                </div>
              </section>
            )}
          </main>
        </div>

        {/* ===== موبايل: Drawer سلايد + أوفرلاي (فقط صغيرة) ===== */}
        <div className="only-mobile" style={styles.overlay(navOpen)} onClick={() => setNavOpen(false)} />
        <aside className="only-mobile mobile-drawer" style={styles.drawer(navOpen)}>
          <nav className="space-y-2">
            <button className="btn-anim" style={styles.navItem(active === 'profile')} onClick={() => { setActive('profile'); setNavOpen(false); }}>
              {isGuest ? 'معلومات الزائر' : 'بيانات المستخدم'}
            </button>
            <button className="btn-anim" style={styles.navItem(active === 'available')} onClick={() => { setActive('available'); setNavOpen(false); }}>
              الكتب المتاحة
            </button>
            <button
              className="btn-anim"
              style={styles.navItem(active === 'borrowed', isGuest)}
              onClick={() => { if (!isGuest) { setActive('borrowed'); setNavOpen(false); } }}
              title={isGuest ? 'متاح للمستخدمين المسجلين فقط' : ''}
              disabled={isGuest}
            >
              الكتب المستعارة
            </button>
            <button
              className="btn-anim"
              style={styles.navItem(active === 'reservations', isGuest)}
              onClick={() => { if (!isGuest) { setActive('reservations'); setNavOpen(false); } }}
              title={isGuest ? 'متاح للمستخدمين المسجلين فقط' : ''}
              disabled={isGuest}
            >
              حجوزاتك
            </button>
            <button className="btn-anim" style={styles.navItem(active === 'about')} onClick={() => { setActive('about'); setNavOpen(false); }}>
              حول المكتبة
            </button>
          </nav>

          {isGuest && (
            <div style={{ marginTop: '1rem', color: colors.muted, fontWeight: 700 }}>
              أنت تتصفح كضيف — لا يمكنك الحجز أو رؤية السجل.
            </div>
          )}
        </aside>

        {/* محتوى الموبايل */}
        <div className="only-mobile mobile-content" style={{ ...styles.content, marginTop: '1rem' }}>
          {active === 'profile' && (
            <section style={styles.card} className="card">
              <h3 style={styles.sectionTitle} className="section-title">
                {isGuest ? '👋 مرحباً بك، زائر' : '📋 بياناتك'}
              </h3>

              {/* 👤 صورة المستخدم / الضيف (موبايل) */}
              <div style={{ ...styles.avatarWrap, justifyContent: 'center' }}>
                <img
                  src={getAvatarSrc(user, isGuest)}
                  alt={isGuest ? 'صورة ضيف' : `صورة ${user?.name || ''}`}
                  style={styles.avatarImg}
                  onError={(e) => { e.currentTarget.src = guestAvatar; }}
                />
              </div>

              {isGuest ? (
                <div style={{ color: colors.muted, fontWeight: 700, lineHeight: 1.9, textAlign: 'center' }}>
                  يمكنك تصفح جميع الكتب المتاحة، ولكن للحجز والاستعارة تحتاج إلى حساب مستخدم.
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px,1fr))', gap: '.75rem' }} className="grid-auto">
                  <div className="card" style={{ ...styles.card, padding: '.8rem' }}>
                    <div style={{ color: colors.muted }}>👤 الاسم</div>
                    <div style={{ fontWeight: 900 }}>{user.name}</div>
                  </div>
                  <div className="card" style={{ ...styles.card, padding: '.8rem' }}>
                    <div style={{ color: colors.muted }}>📱 الهاتف</div>
                    <div style={{ fontWeight: 900 }}>{user.phone || '—'}</div>
                  </div>
                  <div className="card" style={{ ...styles.card, padding: '.8rem' }}>
                    <div style={{ color: colors.muted }}>🏠 العنوان</div>
                    <div style={{ fontWeight: 900 }}>{user.address || '—'}</div>
                  </div>
                  <div className="card" style={{ ...styles.card, padding: '.8rem' }}>
                    <div style={{ color: colors.muted }}>💰 الرصيد</div>
                    <div style={{ fontWeight: 900, color: colors.yellow }}>{fmtIQD(user.balance)}</div>
                  </div>
                </div>
              )}
            </section>
          )}

          {active === 'available' && (
            <section style={{ ...styles.card, display: 'grid', gap: '1rem' }} className="card">
              <h3 style={styles.sectionTitle} className="section-title">📚 الكتب المتاحة</h3>

              <div style={{ display: 'flex', gap: '.6rem', flexWrap: 'wrap', marginBottom: '.6rem' }}>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e)=>setSearchTerm(e.target.value)}
                  placeholder="🔎 ابحث عن اسم كتاب..."
                  style={{
                    flex: '1 1 220px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: 12,
                    padding: '.55rem .75rem',
                    outline: 'none'
                  }}
                />
                <select
                  value={genreFilter}
                  onChange={(e)=>setGenreFilter(e.target.value)}
                  style={{
                    minWidth: 160,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 12,
                    padding: '.55rem .75rem',
                    background: '#fff'
                  }}
                >
                  <option value="">كل الفئات</option>
                  {allGenres.map((g)=>(<option key={g} value={g}>{g}</option>))}
                </select>
                {(searchTerm || genreFilter) && (
                  <button
                    className="btn-anim"
                    onClick={()=>{ setSearchTerm(''); setGenreFilter(''); }}
                    style={styles.moreBtn}
                  >
                    مسح التصفية
                  </button>
                )}
              </div>

              {!searchResultsByGenre ? (
                Object.entries(groupedByGenre).map(([genre, list]) => (
                  <div key={genre}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.5rem' }}>
                      <h4 style={{ ...styles.sectionTitle, borderBottom: 'none', margin: 0, color: colors.orange }} className="section-title">{genre}</h4>
                      <span style={styles.badge}>{list.length} كتاب</span>
                    </div>
                    <GenreGrid list={list} />
                  </div>
                ))
              ) : (
                Object.keys(searchResultsByGenre).length ? (
                  Object.entries(searchResultsByGenre).map(([genre, list]) => (
                    <div key={genre}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.5rem' }}>
                        <h4 style={{ ...styles.sectionTitle, borderBottom: 'none', margin: 0, color: colors.orange }} className="section-title">{genre}</h4>
                        <span style={styles.badge}>{list.length} كتاب</span>
                      </div>
                      <GenreGrid list={list} />
                    </div>
                  ))
                ) : (
                  <p style={{ color: colors.muted }}>لا يوجد نتائج مطابقة لبحثك.</p>
                )
              )}
            </section>
          )}

          {active === 'borrowed' && (
            <section style={styles.card} className="card">
              <h3 style={styles.sectionTitle} className="section-title">📕 الكتب المستعارة</h3>
              {isGuest ? (
                <div style={{ color: colors.muted, fontWeight: 700 }}>
                  هذه الصفحة متاحة فقط للمستخدمين المسجلين.
                </div>
              ) : borrowedBooks.length ? (
                <ul className="space-y-2" style={{ listStyle: 'none', padding: 0 }}>
                  {borrowedBooks.map((b, i) => (
                    <li key={i} style={{ color: colors.muted }}>
                      📖 {b.book?.name || '—'} — 🗓️ يرجع:
                      <span style={{ color: colors.yellow, fontWeight: 800, marginRight: 6 }}>
                        {new Date(b.dueDate).toLocaleDateString()}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p style={{ color: colors.muted }}>لا توجد كتب مستعارة.</p>
              )}
            </section>
          )}

          {active === 'reservations' && (
            <section style={styles.card} className="card">
              <h3 style={styles.sectionTitle} className="section-title">📌 حجوزاتك</h3>
              {isGuest ? (
                <div style={{ color: colors.muted, fontWeight: 700 }}>
                  هذه الصفحة متاحة فقط للمستخدمين المسجلين.
                </div>
              ) : reservations.length ? (
                <ul className="space-y-2" style={{ listStyle: 'none', padding: 0 }}>
                  {reservations.map((r, i) => (
                    <li key={i} style={{ color: colors.muted }}>
                      📚 {r.book?.name || '—'} —{' '}
                      <span style={{ color: colors.text, fontWeight: 800 }}>
                        {statusLabel(r.status)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p style={{ color: colors.muted }}>لا توجد حجوزات بعد.</p>
              )}
            </section>
          )}

          {active === 'about' && (
            <section style={{ ...styles.card }} className="card">
              <h3 style={styles.sectionTitle} className="section-title">ℹ️ حول المكتبة</h3>
              <p style={{ lineHeight: 1.9, color: colors.text, fontWeight: 500 }}>
                هذه المنصّة تابعة لـ<strong> منظمة ملهم</strong>، وتهدف إلى تمكين الشباب
                من الوصول للمعرفة مجاناً عبر استعارة الكتب وحجزها بسهولة على كل الأجهزة.
              </p>
              <div style={styles.aboutGrid} className="grid-auto">
                <div style={styles.aboutItem}><img src={logoImg} alt="شعار ملهم" style={styles.aboutImg} className="about-img" /></div>
                <div style={styles.aboutItem}><img src={footer1Img} alt="صورة توضيحية 1" style={styles.aboutImg} className="about-img" /></div>
                <div style={styles.aboutItem}><img src={footer2Img} alt="صورة توضيحية 2" style={styles.aboutImg} className="about-img" /></div>
              </div>
            </section>
          )}
        </div>
      </div>

      {/* ===== نافذة تفاصيل الكتاب ===== */}
      {selectedBook && (
        <div style={styles.modalOverlay} onClick={() => setSelectedBook(null)}>
          <div style={styles.modalCard} className="modal-card" onClick={(e)=>e.stopPropagation()}>
            <div style={styles.modalHeader} className="modal-header">
              <button className="btn-anim" style={styles.backBtn} onClick={()=>setSelectedBook(null)}>← رجوع</button>
              <div style={{ fontWeight: 900 }}>{selectedBook.name}</div>
              <div style={{ width: 60 }} />
            </div>
            <div style={styles.modalBody} className="modal-body">
              <img
                src={selectedBook.coverUrl || placeholderCover}
                alt={`غلاف ${selectedBook.name || ''}`}
                style={styles.modalImg}
                className="modal-img"
                onError={(e)=>{ e.currentTarget.src = placeholderCover; }}
              />
              <div style={{ display: 'grid', gap: '.6rem', alignContent: 'start' }}>
                <div><strong>الاسم:</strong> {selectedBook.name || '—'}</div>
                <div><strong>المؤلف:</strong> {selectedBook.author || '—'}</div>
                <div><strong>الفئة:</strong> {selectedBook.genre || '—'}</div>
                <div><strong>السعر:</strong> {fmtIQD(selectedBook.price)}</div>
                {selectedBook.barcode && <div><strong>الباركود:</strong> {selectedBook.barcode}</div>}
                {selectedBook.entryDate && (
                  <div><strong>تاريخ الإدخال:</strong> {new Date(selectedBook.entryDate).toLocaleDateString()}</div>
                )}

                {/* شريط الإجراءات — يثبت أسفل النافذة على الموبايل */}
                <div className="modal-actions" style={{ marginTop: '.4rem' }}>
                  {isGuest ? (
                    <button
                      className="btn-anim"
                      style={styles.reserveBtnDisabled}
                      disabled
                      title="يرجى تسجيل الدخول أو الحصول على حساب لإتمام الحجز"
                    >
                      طلب حجز (للمستخدمين فقط)
                    </button>
                  ) : selectedReservationStatus === 'approved' ? (
                    <button style={styles.reserveBtnDisabled} disabled>تم الحجز</button>
                  ) : (
                    <button
                      className="btn-anim"
                      style={styles.reserveBtn}
                      onClick={() => handleReserve(selectedBook._id)}
                    >
                      طلب حجز
                    </button>
                  )}

                  {!!message && (
                    <div style={message.includes('تعذر') ? styles.inlineMsgErr : styles.inlineMsgOk}>
                      {isGuest
                        ? 'يرجى الحصول على حساب مستخدم من المكتبة لإتمام الحجز.'
                        : message.includes('تعذر')
                        ? 'حدث خطأ، حاول مجددًا'
                        : 'تم إرسال طلب الحجز بنجاح'}
                    </div>
                  )}

                  {selectedReservationStatus === 'approved' && !isGuest && (
                    <div style={styles.inlineMsgOk}>&#10004; تم قبول حجز هذا الكتاب</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
